import { Link } from 'react-router-dom';
import { PiArrowSquareOutBold } from 'react-icons/pi';
import V2Modal from '@v2/components/ui/V2Modal';
import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';
import { fmtMonthShort } from './CommissionExecutiveCharts';
import type { SellerCommissionForecastMonth, SellerForecastInstallmentItem } from './types';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

const brlShort = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

function formatDue(date: string | null) {
    if (!date) return '—';
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
}

type DealGroup = {
    deal_id: number;
    deal_number: string | null;
    deal_title: string | null;
    deal_amount: number;
    commission_rate_pp: number;
    commission_total: number;
    installment_total: number;
    items: SellerForecastInstallmentItem[];
};

function groupByDeal(items: SellerForecastInstallmentItem[]): DealGroup[] {
    const map = new Map<number, DealGroup>();

    for (const item of items) {
        let group = map.get(item.deal_id);
        if (!group) {
            group = {
                deal_id: item.deal_id,
                deal_number: item.deal_number,
                deal_title: item.deal_title,
                deal_amount: item.deal_amount,
                commission_rate_pp: item.commission_rate_pp,
                commission_total: 0,
                installment_total: 0,
                items: [],
            };
            map.set(item.deal_id, group);
        }
        group.commission_total += item.commission_amount;
        group.installment_total += item.installment_amount;
        group.items.push(item);
    }

    return [...map.values()]
        .map((g) => ({
            ...g,
            items: [...g.items].sort((a, b) => a.installment_number - b.installment_number),
        }))
        .sort((a, b) => b.commission_total - a.commission_total);
}

type Props = {
    month: SellerCommissionForecastMonth | null;
    onClose: () => void;
};

export default function ForecastMonthDetailModal({ month, onClose }: Props) {
    const open = !!month;
    const items = month?.items ?? [];
    const deals = groupByDeal(items);
    return (
        <V2Modal
            open={open}
            onClose={onClose}
            size="5xl"
            title={month ? `Comissão prevista · ${fmtMonthShort(month.month)}` : 'Mês'}
            description={
                month
                    ? `${brlShort(month.amount)} de comissão · ${month.installments_count} parcela(s) · ${month.deals_count ?? deals.length} deal(s)`
                    : undefined
            }
        >
            {!month || items.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhuma parcela de comissão neste mês.</p>
            ) : (
                <div className="space-y-4">
                    <p className="rounded-lg bg-orange-50 px-3 py-2 text-xs text-orange-800 dark:bg-orange-950/40 dark:text-orange-200">
                        Os valores em destaque são a <strong>sua comissão</strong> (parcela do cliente × taxa).
                        O valor da parcela do cliente aparece como referência.
                    </p>

                    <div className="space-y-3">
                        {deals.map((deal) => (
                            <article
                                key={deal.deal_id}
                                className="overflow-hidden rounded-xl border border-gray-200 dark:border-zinc-700"
                            >
                                <header className="flex flex-wrap items-start justify-between gap-2 bg-gray-50 px-4 py-3 dark:bg-zinc-800/80">
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                                            {deal.deal_title || `Deal #${deal.deal_number ?? deal.deal_id}`}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            #{deal.deal_number ?? deal.deal_id}
                                            {' · '}Deal {brlShort(deal.deal_amount)}
                                            {' · '}{deal.commission_rate_pp.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3 text-right">
                                        <div>
                                            <p className="text-[10px] uppercase text-gray-400">Comissão no mês</p>
                                            <p className="text-sm font-bold text-orange-700 dark:text-orange-300">
                                                {brl(deal.commission_total)}
                                            </p>
                                        </div>
                                        <Link
                                            to={ploomesDealPath({ id: deal.deal_id, internal_number: deal.deal_number })}
                                            className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700"
                                        >
                                            Abrir
                                            <PiArrowSquareOutBold className="size-3.5" />
                                        </Link>
                                    </div>
                                </header>

                                <table className="w-full text-sm">
                                    <thead className="border-b border-gray-100 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:border-zinc-800">
                                        <tr>
                                            <th className="px-4 py-2">Parcela</th>
                                            <th className="px-4 py-2">Vencimento</th>
                                            <th className="px-4 py-2 text-right">Parcela cliente</th>
                                            <th className="px-4 py-2 text-right">Sua comissão</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50 dark:divide-zinc-800">
                                        {deal.items.map((item) => (
                                            <tr key={item.installment_id} className="hover:bg-gray-50/80 dark:hover:bg-zinc-800/40">
                                                <td className="px-4 py-2.5">
                                                    <span className="font-medium text-gray-800 dark:text-zinc-100">
                                                        {item.installment_number === 0
                                                            ? ((item.entry_index ?? 0) > 0 ? `Entrada ${item.entry_index}` : 'Entrada')
                                                            : `${item.installment_number}/${item.total_installments || '—'}`}
                                                    </span>
                                                    {item.is_overdue && (
                                                        <span className="ml-2 rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                                                            Vencida
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5 text-gray-600 dark:text-zinc-300">
                                                    {formatDue(item.due_date)}
                                                </td>
                                                <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                                                    {brl(item.installment_amount)}
                                                </td>
                                                <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-orange-700 dark:text-orange-300">
                                                    {brl(item.commission_amount)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </article>
                        ))}
                    </div>
                </div>
            )}
        </V2Modal>
    );
}
